import { useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { Phone, Shield, CheckCircle2, ArrowLeft, AlertCircle } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "../components/ui/input-otp";
import Header from "../components/Header";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ClaimListing = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [phone, setPhone] = useState("");
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [mockCode, setMockCode] = useState("");
  const [error, setError] = useState("");

  const handleSendCode = async () => {
    if (!phone || phone.length < 10) {
      setError("Introdu un număr de telefon valid");
      return;
    }
    
    setLoading(true);
    setError("");
    
    try {
      const formattedPhone = phone.startsWith("+") ? phone : `+40${phone.replace(/^0/, "")}`;
      const response = await axios.post(`${API}/verification/send`, { phone: formattedPhone });
      
      if (response.data.mock_code) {
        setMockCode(response.data.mock_code);
        toast.info(`Cod de verificare (simulare): ${response.data.mock_code}`);
      } else {
        toast.success("Cod SMS trimis!");
      }
      
      setPhone(formattedPhone);
      setStep(2);
    } catch (error) {
      console.error("Error sending code:", error);
      setError(error.response?.data?.detail || "Eroare la trimiterea codului");
      toast.error("Eroare la trimiterea codului SMS");
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyCode = async () => {
    if (code.length !== 6) {
      setError("Introdu codul complet de 6 cifre");
      return;
    }
    
    setLoading(true);
    setError("");
    
    try {
      await axios.post(`${API}/verification/check`, { phone, code });
      
      // Claim the listing
      await axios.post(`${API}/listings/${id}/claim?phone=${encodeURIComponent(phone)}`);
      
      setStep(3);
      toast.success("Anunț revendicat cu succes!");
    } catch (error) {
      console.error("Error verifying:", error);
      setError(error.response?.data?.detail || "Cod invalid sau eroare la revendicare");
      toast.error("Verificare eșuată");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-stone-100">
      <Header />
      
      <main className="max-w-lg mx-auto px-4 py-12">
        <Link to={`/proprietate/${id}`} className="text-stone-500 hover:text-stone-700 flex items-center gap-1 mb-8">
          <ArrowLeft className="w-4 h-4" />
          Înapoi la anunț
        </Link>

        <div className="bg-white rounded-2xl p-6 sm:p-8 card-shadow">
          {/* Step Indicator */}
          <div className="flex items-center justify-center mb-8">
            {[1, 2, 3].map((s) => (
              <div key={s} className="flex items-center">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold transition-all ${
                  step >= s 
                    ? "bg-emerald-950 text-white" 
                    : "bg-stone-100 text-stone-400"
                }`}>
                  {step > s ? <CheckCircle2 className="w-5 h-5" /> : s}
                </div>
                {s < 3 && (
                  <div className={`w-12 h-1 transition-all ${
                    step > s ? "bg-emerald-950" : "bg-stone-200"
                  }`} />
                )}
              </div>
            ))}
          </div>

          {/* Step 1: Phone Number */}
          {step === 1 && (
            <div className="animate-fade-in" data-testid="step-phone">
              <div className="text-center mb-8">
                <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Phone className="w-8 h-8 text-emerald-700" />
                </div>
                <h2 className="font-['Outfit'] text-2xl font-bold text-stone-900 mb-2">
                  Revendică anunțul
                </h2>
                <p className="text-stone-500">
                  Introdu numărul de telefon din anunțul original pentru verificare
                </p>
              </div>

              <div className="space-y-4">
                <div>
                  <Label htmlFor="phone">Număr de telefon</Label>
                  <Input
                    id="phone"
                    data-testid="phone-input"
                    type="tel"
                    placeholder="0721 000 000"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    className="h-12 rounded-xl mt-2"
                  />
                </div>

                {error && (
                  <div className="flex items-center gap-2 text-red-600 text-sm">
                    <AlertCircle className="w-4 h-4" />
                    {error}
                  </div>
                )}

                <Button
                  data-testid="send-code-button"
                  onClick={handleSendCode}
                  disabled={loading}
                  className="w-full h-12 bg-emerald-950 hover:bg-emerald-900 rounded-xl"
                >
                  {loading ? "Se trimite..." : "Trimite cod SMS"}
                </Button>
              </div>
            </div>
          )}

          {/* Step 2: Verify Code */}
          {step === 2 && (
            <div className="animate-fade-in" data-testid="step-verify">
              <div className="text-center mb-8">
                <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Shield className="w-8 h-8 text-amber-700" />
                </div>
                <h2 className="font-['Outfit'] text-2xl font-bold text-stone-900 mb-2">
                  Verifică codul
                </h2>
                <p className="text-stone-500">
                  Am trimis un cod de 6 cifre la {phone}
                </p>
                {mockCode && (
                  <p className="text-amber-600 text-sm mt-2 bg-amber-50 p-2 rounded-lg">
                    Mod simulare - Codul tău: <strong>{mockCode}</strong>
                  </p>
                )}
              </div>

              <div className="space-y-6">
                <div className="flex justify-center">
                  <InputOTP
                    data-testid="otp-input"
                    maxLength={6}
                    value={code}
                    onChange={setCode}
                  >
                    <InputOTPGroup>
                      <InputOTPSlot index={0} />
                      <InputOTPSlot index={1} />
                      <InputOTPSlot index={2} />
                      <InputOTPSlot index={3} />
                      <InputOTPSlot index={4} />
                      <InputOTPSlot index={5} />
                    </InputOTPGroup>
                  </InputOTP>
                </div>

                {error && (
                  <div className="flex items-center justify-center gap-2 text-red-600 text-sm">
                    <AlertCircle className="w-4 h-4" />
                    {error}
                  </div>
                )}

                <Button
                  data-testid="verify-button"
                  onClick={handleVerifyCode}
                  disabled={loading || code.length !== 6}
                  className="w-full h-12 bg-emerald-950 hover:bg-emerald-900 rounded-xl"
                >
                  {loading ? "Se verifică..." : "Verifică și revendică"}
                </Button>

                <button
                  onClick={() => setStep(1)}
                  className="w-full text-stone-500 hover:text-stone-700 text-sm"
                >
                  Schimbă numărul de telefon
                </button>
              </div>
            </div>
          )}

          {/* Step 3: Success */}
          {step === 3 && (
            <div className="animate-fade-in text-center" data-testid="step-success">
              <div className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle2 className="w-10 h-10 text-emerald-700" />
              </div>
              <h2 className="font-['Outfit'] text-2xl font-bold text-stone-900 mb-2">
                Felicitări!
              </h2>
              <p className="text-stone-500 mb-8">
                Anunțul tău a fost revendicat cu succes. Acum poți să îl editezi și să adaugi mai multe detalii.
              </p>

              <div className="space-y-3">
                <Button
                  data-testid="go-to-dashboard-button"
                  onClick={() => navigate("/dashboard")}
                  className="w-full h-12 bg-emerald-950 hover:bg-emerald-900 rounded-xl"
                >
                  Mergi la panoul tău
                </Button>
                <Button
                  variant="outline"
                  onClick={() => navigate(`/dashboard/edit/${id}`)}
                  className="w-full h-12 rounded-xl"
                >
                  Editează anunțul acum
                </Button>
              </div>
            </div>
          )}
        </div>

        {/* Info Card */}
        <div className="mt-6 bg-blue-50 border border-blue-200 rounded-xl p-4">
          <h4 className="font-semibold text-blue-900 mb-2">De ce să revendici anunțul?</h4>
          <ul className="text-blue-700 text-sm space-y-1">
            <li>• Publicare gratuită și profesională</li>
            <li>• Descriere generată de AI</li>
            <li>• Expunere către cumpărători eligibili pentru credit</li>
            <li>• Control total asupra anunțului tău</li>
          </ul>
        </div>
      </main>
    </div>
  );
};

export default ClaimListing;
